
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from '../i18n/useTranslation';
import { InstagramIcon, PinterestIcon, TikTokIcon, TwitterIcon, SendIcon } from './icons';
import { AppPage } from '../types';
import { APP_CONFIG, SOCIAL_LINKS } from '../config';
import Logo from './Logo';

interface FooterProps {
    onNavigate: (page: AppPage, params?: Record<string, string>) => void;
}

const Footer: React.FC<FooterProps> = ({ onNavigate }) => {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [subscribeStatus, setSubscribeStatus] = useState<'idle' | 'success' | 'error'>('idle');

    const handleSubscribe = useCallback((e: React.FormEvent) => {
        e.preventDefault();
        if (!email || !/\S+@\S+\.\S+/.test(email)) {
            setSubscribeStatus('error');
            setTimeout(() => setSubscribeStatus('idle'), 3000);
            return;
        }
        // Simulate API call
        console.log('Subscribing email:', email);
        setSubscribeStatus('success');
        setEmail('');
        setTimeout(() => setSubscribeStatus('idle'), 3000);
    }, [email]);


    return (
        <footer className={`bg-white/90 backdrop-blur-xl border-t border-gray-200/50 py-12 z-50 dark:bg-gray-900/90 dark:border-gray-800/50`}>
            <div className="mx-auto max-w-[1400px] px-6">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12 text-sm text-gray-700 dark:text-gray-300">

                    {/* Brand & Social - 4 Cols */}
                    <div className="md:col-span-4 flex flex-col items-start gap-6">
                        <Logo className="h-8 w-auto text-gray-900 dark:text-white" />
                        <p className="text-gray-500 dark:text-gray-400 max-w-sm leading-relaxed">
                            Experience the future of fashion. Your personal AI stylist, ready to transform your wardrobe.
                        </p>
                        <div className="flex gap-4 mt-2">
                            <a href={SOCIAL_LINKS.INSTAGRAM} target="_blank" rel="noopener noreferrer" className="p-2 bg-gray-100 dark:bg-gray-800 rounded-full text-gray-600 hover:text-black dark:text-gray-400 dark:hover:text-white hover:bg-gray-200 transition-all">
                                <InstagramIcon className="w-5 h-5" />
                            </a>
                            <a href={SOCIAL_LINKS.PINTEREST} target="_blank" rel="noopener noreferrer" className="p-2 bg-gray-100 dark:bg-gray-800 rounded-full text-gray-600 hover:text-black dark:text-gray-400 dark:hover:text-white hover:bg-gray-200 transition-all">
                                <PinterestIcon className="w-5 h-5" />
                            </a>
                            <a href={SOCIAL_LINKS.TIKTOK} target="_blank" rel="noopener noreferrer" className="p-2 bg-gray-100 dark:bg-gray-800 rounded-full text-gray-600 hover:text-black dark:text-gray-400 dark:hover:text-white hover:bg-gray-200 transition-all">
                                <TikTokIcon className="w-5 h-5" />
                            </a>
                            <a href={SOCIAL_LINKS.TWITTER} target="_blank" rel="noopener noreferrer" className="p-2 bg-gray-100 dark:bg-gray-800 rounded-full text-gray-600 hover:text-black dark:text-gray-400 dark:hover:text-white hover:bg-gray-200 transition-all">
                                <TwitterIcon className="w-5 h-5" />
                            </a>
                        </div>
                    </div>

                    {/* Navigation Links - 4 Cols */}
                    <div className="md:col-span-4 grid grid-cols-2 gap-8">
                        <div>
                            <h4 className="text-xs font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-6">{t('footer_company')}</h4>
                            <ul className="space-y-4">
                                <li><button onClick={() => onNavigate('about')} className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">{t('nav_about')}</button></li>
                                <li><button onClick={() => onNavigate('press')} className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">Press</button></li>
                                <li><button onClick={() => onNavigate('portfolio')} className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">{t('nav_portfolio')}</button></li>
                                <li><button onClick={() => onNavigate('blog')} className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">{t('nav_blog')}</button></li>
                                <li><button onClick={() => onNavigate('contact' as any)} className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">Contact</button></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="text-xs font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-6">{t('footer_legal')}</h4>
                            <ul className="space-y-4">
                                <li><button onClick={() => onNavigate('legal', { '#': 'privacy' })} className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">{t('nav_privacy')}</button></li>
                                <li><button onClick={() => onNavigate('legal', { '#': 'terms' })} className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">{t('nav_terms')}</button></li>
                                <li><button onClick={() => onNavigate('deployment-guide')} className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">Developers</button></li>
                            </ul>
                        </div>
                    </div>

                    {/* Newsletter - 4 Cols */}
                    <div className="md:col-span-4">
                        <h4 className="text-xs font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-6">{t('footer_subscribe_title')}</h4>
                        <p className="text-gray-500 dark:text-gray-400 mb-4 text-sm">{t('footer_subscribe_desc')}</p>
                        <form onSubmit={handleSubscribe} className="relative max-w-sm">
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder={t('footer_subscribe_placeholder')}
                                className="w-full py-3 pl-4 pr-12 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition outline-none"
                            />
                            <button type="submit" aria-label={t('footer_subscribe_button_aria')} className="absolute right-1 top-1 p-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition shadow-md">
                                <SendIcon className="w-4 h-4" />
                            </button>
                        </form>
                        <AnimatePresence>
                            {subscribeStatus !== 'idle' && (
                                <motion.p
                                    initial={{ opacity: 0, y: 5 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0 }}
                                    className={`text-xs mt-3 font-semibold ${subscribeStatus === 'success' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}
                                >
                                    {subscribeStatus === 'success' ? t('footer_subscribe_success') : t('footer_subscribe_error')}
                                </motion.p>
                            )}
                        </AnimatePresence>
                    </div>
                </div>

                <div className="mt-16 pt-8 border-t border-gray-200/50 dark:border-gray-800/50 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-gray-400 dark:text-gray-500">
                    <p>
                        &copy; {new Date().getFullYear()} {t('appName')}. {t('footer_copyright')}
                        <span className="opacity-50 ml-2">v{APP_CONFIG.VERSION}</span>
                    </p>
                    <p>
                        {t('footer_createdBy')}{' '}
                        <a href="https://x.com/dumian" target="_blank" rel="noopener noreferrer" className="font-semibold hover:text-indigo-500 transition-colors">@dumian</a>
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
